import React, { startTransition, useMemo, useRef, useSyncExternalStore } from 'react';
import * as ReactRouter from 'react-router';
import { createBrowserHistory, History, Location } from 'history';

const RouterContext = ReactRouter.__RouterContext;
// Runtime export; missing from @types/react-router.
const HistoryContext = (ReactRouter as unknown as {
  __HistoryContext: React.Context<History>;
}).__HistoryContext;

type React18BrowserRouterProps = {
  children?: React.ReactNode;
  basename?: string;
  forceRefresh?: boolean;
  keyLength?: number;
  getUserConfirmation?: (
    message: string,
    callback: (result: boolean) => void,
  ) => void;
};

const computeRootMatch = (pathname: string) => ({
  path: '/',
  url: '/',
  params: {},
  isExact: pathname === '/',
});

/** Wrap history mutations so Suspense keeps the previous page painted while lazy chunks load. */
const withStartTransition = (history: History): History => {
  const push = history.push.bind(history);
  const replace = history.replace.bind(history);
  const go = history.go.bind(history);

  history.push = (...args: Parameters<History['push']>) => {
    startTransition(() => {
      push(...args);
    });
  };
  history.replace = (...args: Parameters<History['replace']>) => {
    startTransition(() => {
      replace(...args);
    });
  };
  history.go = (...args: Parameters<History['go']>) => {
    startTransition(() => {
      go(...args);
    });
  };

  return history;
};

/**
 * RR5-compatible BrowserRouter that subscribes to history via
 * React 18 `useSyncExternalStore`, so URL changes always re-render
 * the route tree under `createRoot` (stock RR5 BrowserRouter often won't).
 */
const React18BrowserRouter = function React18BrowserRouter({
  children,
  basename,
  forceRefresh,
  keyLength,
  getUserConfirmation,
}: React18BrowserRouterProps) {
  const historyRef = useRef<History | null>(null);
  if (!historyRef.current) {
    historyRef.current = withStartTransition(createBrowserHistory({
      basename,
      forceRefresh,
      keyLength,
      getUserConfirmation,
    }));
  }
  const history = historyRef.current;

  const subscribe = useMemo(
    () => (onStoreChange: () => void) => history.listen(() => onStoreChange()),
    [history],
  );

  const getSnapshot = () => history.location;

  const location = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  ) as Location;

  const match = useMemo(
    () => computeRootMatch(location.pathname),
    [location.pathname],
  );

  const routing = useMemo(
    () => ({
      history,
      location,
      match,
      staticContext: undefined as undefined,
    }),
    [history, location, match],
  );

  return (
    <RouterContext.Provider value={routing}>
      <HistoryContext.Provider value={history}>
        {children}
      </HistoryContext.Provider>
    </RouterContext.Provider>
  );
};

export default React18BrowserRouter;
